import { ROLE_PROFILE_TYPES } from "views_shared/app/javascript/constants/profileTypes";
import { determinePaginationParams, readUrl, getResource, updateResource } from "./api";

const get = async (id) => {
  const url = readUrl("pitches", { selector: `id=eq.${id}` });
  return getResource(url);
};

const getByProfileSlug = async (profileSlug, options = { page: 1, per_page: 50 }) => {
  const selector = `profile_slug=eq.${profileSlug}&sent_at=not.is.null&draft=is.false`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("pitches", { selector, paginationParams });
  return getResource(url);
};

// V1->V2 id bridge for the V1-hosted pitch inbox: V1 pitch ids are V2 migrate_ids.
// The conversation header needs only the sources, not the whole pitch row, so the global
// inbox can ask for that one column instead of re-fetching what PitchAsMessage already has.
const getPitchedEntities = async (id) => {
  const url = readUrl("pitches", { selector: `select=pitched_entities&id=eq.${id}` });
  return getResource(url);
};

const getByMigrateIds = async (migrateIds) => {
  if (!migrateIds.length) return { data: [] };
  const selector = `select=id,migrate_id,profile_id,read_at,reaction,pitch_attachments,has_video&migrate_id=in.(${migrateIds.join(",")})`;
  const url = readUrl("pitches", { selector });
  return getResource(url);
};

// Sent pitches only — reporters must never see drafts (RLS also enforces this).
const getByRequestId = async (requestId) => {
  const selector = `request_id=eq.${requestId}&sent_at=not.is.null&order=sent_at.desc`;
  const url = readUrl("pitches", { selector });
  return getResource(url);
};

// A mute hides the muted user's pitches created after it; a timed mute only those created
// inside its window. Same rule as V1's SourceRequest.filter_muted_pitches, which the
// request page applies server-side; here it becomes a PostgREST exclusion per mute.
const muteWindow = ({ createdAt, durationDays }) => {
  const from = new Date(createdAt);
  const to = durationDays > 0 ? new Date(from.getTime() + durationDays * 86400000) : null;
  return { from, to };
};

const muteExclusionClause = (mute) => {
  const { from, to } = muteWindow(mute);
  const bounds = [`created_at.gt.${from.toISOString()}`];
  if (to) bounds.push(`created_at.lt.${to.toISOString()}`);
  return `not.and(user_migrate_id.eq.${mute.userId},${bounds.join(",")})`;
};

export const hiddenByMute = (pitch, mutes) => {
  if (!mutes?.length || !pitch?.user_migrate_id || !pitch?.created_at) return false;
  const createdAt = new Date(pitch.created_at);
  return mutes.some((mute) => {
    if (String(mute.userId) !== String(pitch.user_migrate_id)) return false;
    const { from, to } = muteWindow(mute);
    return createdAt > from && (!to || createdAt < to);
  });
};

/**
 * All sent pitches across every request the media profile owns — the virtual-row source
 * for the inbox Everything tab. Mirrors conversations.list's compound keyset so the two
 * streams paginate the same way.
 * @param {number} mediaProfileId - The request owner's numeric profile id
 * @param {object} options.cursor - Keyset cursor: `{ sentAt, id }` for the next page; null = first page
 * @param {number} options.limit - Page size (default 20)
 * @param {object} options.filters - Inbox filters: { status: "unread"|"read"|null,
 *   role: "experts"|"prs"|"media"|null, posts: <request id>|null,
 *   mutes: [{ userId, createdAt, durationDays }] } — status/role/posts share the
 *   conversations list's semantics; mutes are the reporter's V1 mutes (V1 pages only).
 */
const listSentOnOwnRequests = async (mediaProfileId, { cursor = null, limit = 20, filters = null } = {}) => {
  const cursorFilter = cursor
    ? [["or", `(sent_at.lt.${cursor.sentAt},and(sent_at.eq.${cursor.sentAt},id.lt.${cursor.id}))`]]
    : [];

  const filterParams = [];
  if (filters?.status === "unread") {
    filterParams.push(["read_at", "is.null"]);
  } else if (filters?.status === "read") {
    filterParams.push(["read_at", "not.is.null"]);
  }
  const roleTypes = ROLE_PROFILE_TYPES[filters?.role];
  if (roleTypes) {
    filterParams.push(["type_of_profile", `in.(${roleTypes.join(",")})`]);
  }
  if (filters?.posts) {
    filterParams.push(["request_id", `eq.${filters.posts}`]);
  }
  const muteClauses = (filters?.mutes || []).map(muteExclusionClause);
  if (muteClauses.length) {
    filterParams.push(["and", `(${muteClauses.join(",")})`]);
  }

  const params = new URLSearchParams([
    ["request_media_person_id", `eq.${mediaProfileId}`],
    ["sent_at", "not.is.null"],
    ["order", "sent_at.desc,id.desc"],
    ["limit", String(limit)],
    ...cursorFilter,
    ...filterParams,
  ]);

  const url = readUrl("pitches", { selector: params.toString() });
  return getResource(url);
};

// The promoter side of the Posts/"Opportunity" filter: the requests they have PITCHED on.
// (Media use requests.listOwnedWithPitches — one row per request. There is no equivalent
// request-grain source for pitchers, so this stays per-pitch and pages instead: PostgREST
// clamps any limit above db-max-rows (1,000 in production), so paging is the only way to
// see past it — 97 promoters have more than 1,000 sent pitches.)
// Most-recently-pitched first for the dropdown; pages arrive in id order.
const sortBySentAtDesc = (rows) =>
  [...rows].sort((a, b) => String(b.sent_at || "").localeCompare(String(a.sent_at || "")));

const PITCH_PAGE = 1000;
// Keyset, NOT offset. PostgREST validates an offset range against an ESTIMATED row count and
// answers 416 once the offset passes it — an estimate that can sit far below the real total,
// silently truncating the list. Keying off the last pitch id seen removes that dependency.
// The bound stands in for the short-page exit if the server ever ignores the cursor:
// 20 pages = 20,000 rows, far above any real profile.
const MAX_PAGES = 20;

const listPitchedRequestOptions = async (promoterProfileId) => {
  const rows = [];
  let cursorId = null;
  for (let pageNo = 0; pageNo < MAX_PAGES; pageNo += 1) {
    const params = new URLSearchParams([
      ["profile_id", `eq.${promoterProfileId}`],
      ["sent_at", "not.is.null"],
      ["select", "id,request_id,request_title,sent_at"],
      // Paging by pitch id keeps the cursor total (sent_at can tie); the caller dedupes to
      // one row per request and display order is restored below.
      ["order", "id.desc"],
      ["limit", String(PITCH_PAGE)],
      ...(cursorId ? [["id", `lt.${cursorId}`]] : []),
    ]);
    // eslint-disable-next-line no-await-in-loop -- keyset pages are sequential by nature
    const { data, error } = await getResource(readUrl("pitches", { selector: params.toString() }));
    if (error) return { data: sortBySentAtDesc(rows), error };
    // getResource maps a 416 (PostgREST count=estimated pagination discrepancy) to a
    // non-array `data` — spreading that would throw, so treat any non-array as the end.
    if (!Array.isArray(data)) return { data: sortBySentAtDesc(rows) };
    rows.push(...data);
    if (data.length < PITCH_PAGE) return { data: sortBySentAtDesc(rows) };
    cursorId = data[data.length - 1].id;
  }
  return { data: sortBySentAtDesc(rows) }; // bound hit
};

// Client-side mirror of listSentOnOwnRequests' filter params — used to decide whether a
// selected pitch still belongs after a filter change (the pitch counterpart of
// useConversations' convMatchesFilters). Keep in lockstep with the params above.
const matchesFilters = (pitch, filters) => {
  if (filters?.status === "unread" && pitch.read_at) return false;
  if (filters?.status === "read" && !pitch.read_at) return false;
  const roleTypes = ROLE_PROFILE_TYPES[filters?.role];
  if (roleTypes && !roleTypes.includes(pitch.type_of_profile)) return false;
  if (filters?.posts && String(pitch.request_id) !== String(filters.posts)) return false;
  if (hiddenByMute(pitch, filters?.mutes)) return false;
  return true;
};

// Read-marking is the request owner's action; addressed by V2 pitch id (works for
// V2-native pitches too, unlike the migrate_id-keyed pitch_replies convention).
const markReadById = async (id) => {
  const url = `/interact/api/messaging/pitches/${id}/read`;
  return updateResource(url, {});
};

// Like/dislike is the request owner's private triage mark; null clears it. V2 pitch id.
const setReactionById = async (id, reaction) => {
  const url = `/interact/api/messaging/pitches/${id}/reaction`;
  return updateResource(url, { reaction });
};

// "Sent from" per V1 pitch id: the geolocation of the session the pitch was created in
const getSentFromByMigrateIds = async (migrateIds) => {
  const { data, error } = await getResource(`/v1_link/api/pitches/sent_from?migrate_ids=${migrateIds.join(",")}`);
  if (error) return { error };
  return { data: (data && data.data) || {} };
};

export default {
  get,
  getByMigrateIds,
  getPitchedEntities,
  getByProfileSlug,
  getByRequestId,
  getSentFromByMigrateIds,
  listPitchedRequestOptions,
  listSentOnOwnRequests,
  markReadById,
  matchesFilters,
  setReactionById,
};
